<i18n src="./translations"></i18n>

<template>
  <div :class="classes" class="plp-product-21" :data-preload="[$options.name]">
    <div
      class="media"
      @mouseenter="titleHover(true)"
      @mouseleave="titleHover(false)"
    >
      <div
        class="plp-product-21__title"
        role="button"
        tabindex="0"
        :aria-label="`${product.brandName} ${product.name}`"
        @click="productClick()"
        @keydown.enter.prevent="productClick()"
        @keydown.space.prevent="productClick()"
      >
        {{ product.brandName }} {{ product.name }}
      </div>

      <span class="productId">
        {{ $t('plp-product-21.article_number') }} {{ product.displaySku }}
      </span>

      <span v-if="isSkuDisplayed" class="productSku">
        | SKU:
        <input
          type="text"
          :value="product.sku"
          readonly
          :aria-label="$t('plp-product-21.product-sku')"
          @focus="$event.target.select()"
        />
      </span>
    </div>

    <div class="imageContainer qa-ProductCard__images">
      <div
        class="plp-product-21__images"
        :class="{ active: imageHover }"
        @click="!product.image.length && productClick()"
      >
        <AdvancedImage
          v-if="product.image.length"
          class="plp-product-21__product-image qa-ProductCard__image"
          :src="product.image"
          :alt="product.image.alt || ''"
          :config="{ q: 80, w: 180 }"
          @image-click="productClick()"
        />
        <NoImageSvg
          v-else
          class="plp-product-21__no-image"
          :lazy="true"
          :inline="false"
          @click="productClick()"
        />

        <AdvancedImage
          v-if="product.brandLogoUrl"
          class="plp-product-21__brand qa-ProductCard__logo"
          :src="product.brandLogoUrl"
          :alt="product.brandName"
          :config="{ q: 80, w: 100 }"
        />
      </div>
      <div
        v-if="product.documents.items.length"
        class="plp-product-21__documents"
      >
        <div
          v-for="(document, index) in product.documents.items"
          :key="index"
          class="plp-product-21__document"
          @click="handleDocumentClick(index, $event)"
        >
          <Icon
            class="plp-product-21__document-icon"
            :name="processIconName(document.icon)"
          />
          <MaskedLink
            ref="documentLink"
            class="plp-product-21__document-link"
            :redirect-to="document.url"
            :text="document.title"
            target="_blank"
            rel="noreferrer noopener"
          />
        </div>
      </div>
    </div>

    <div class="contentContainer">
      <div class="isDesktop">
        <div v-show="cleanResouceType" class="foundByIdentifier">
          <span class="icon">
            <Icon name="noted_outlined" />
          </span>
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="foundByText"></span>
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <ProductInfoSections
          :product="product"
          variant="plp"
          :car-limit="carAttributesLimit"
        />

        <TyreLabels
          is-minimal
          :attributes="product.attributes"
          :images="product.images"
        ></TyreLabels>

        <ProductBundles v-if="productBundles.length" :items="productBundles" />
      </div>

      <div class="price isMobile">
        <PriceInfo21 ref="priceInfo" modifier="slim" :product="product" />
      </div>
    </div>

    <div class="plp-product-21__cta-container">
      <div class="price isDesktop">
        <PriceInfo21 ref="priceInfo" modifier="slim" :product="product" />
      </div>

      <div v-show="cleanResouceType" class="foundByIdentifier isMobile">
        <span class="icon">
          <Icon name="noted_outlined" />
        </span>
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="foundByText"></span>
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <PdpVehicleNotification
        v-if="!product.isDropShipment"
        class="plp-product-21__vehicle-notification"
        :gtm-class-name="'gtm--plp-vehicle-notification'"
        :vehicle-compatibility="productVehicleCompatibility"
        :vehicle-name="vehicleName"
      />

      <PdpActions
        class="plp-product-21__actions"
        :product="product"
        :add-to-cart-button-props="
          getAddToCartButtonProps({
            add: $t('plp-product-21.cart.add'),
            added: $t('plp-product-21.cart.added'),
          })
        "
        :is-adding-to-cart="addToCartState === AddToCartState.Loading"
        @cart-click="handleAddToCart"
        @wishlist-click="handleAddToWishlist"
        @quantity-change="handleQuantityChange"
      />

      <Button
        class="plp-product-21__show-details"
        :class="detailsButtonClass"
        :modifiers="['full', 'iconRight']"
        :on-click="productClick"
        :title="this.$t('plp-product-21.details')"
      />

      <PlpDescription21
        v-if="shouldShowDescription"
        :description="product.shortDescription"
        class="isMobile"
      />

      <Dialog
        ref="unavailableProductDialog"
        :click-outside="true"
        :title="$t('plp-product-21.availability.delayed.title')"
      >
        <div>
          {{
            $t('plp-product-21.availability.delayed.message', {
              '%customerServicePhoneNumber%': customerServicePhoneNumber,
            })
          }}
        </div>
      </Dialog>
    </div>

    <ProductAvailability :product-availability="product.productAvailability" />

    <div class="further-details isMobile">
      <ProductInfoSections
        :product="product"
        variant="plp"
        :car-limit="carAttributesLimit"
        :include-description="false"
      />

      <TyreLabels
        is-minimal
        :attributes="product.attributes"
        :images="product.images"
      ></TyreLabels>
      <ProductBundles v-if="productBundles.length" :items="productBundles" />
    </div>

    <AlternativeProductsDialog
      ref="alternativeProductsDialog"
      :parent-product="product"
      :handle-cancel="handleAlternativeProductCancel"
      :handle-confirm="handleAlternativeProductConfirm"
    />
  </div>
</template>

<script>
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { mapActions, mapState } from 'vuex';
import globalMixin from 'Libs/mixins/globalMixin.js';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';
import AdvancedImage from 'Components/01-atoms/advanced-image/AdvancedImage.vue';
import ProductInfoSections from 'Src/components/02-molecules/product-info-sections/ProductInfoSections.vue';
import PdpActions from 'Components/02-molecules/pdp-actions/PdpActions';
import ProductAvailability from 'Components/02-molecules/product-availability/ProductAvailability';
import PriceInfo21 from 'Components/02-molecules/price-info-21/PriceInfo21.vue';
import Button from 'Components/01-atoms/button/Button.vue';
import NoImageSvg from 'Components/00-generated/NoImageSvg.vue';
import Dialog from 'Components/02-molecules/dialog/Dialog.vue';
import PlpDescription21 from 'Components/02-molecules/plp-description-21/PlpDescription21';
import Icon from 'Components/00-generated/Icon';
import MaskedLink from 'Components/01-atoms/masked-link/MaskedLink';
import ProductBundles from 'Components/02-molecules/product-bundles/ProductBundles';
import TyreLabels from 'Components/02-molecules/tyre-labels/TyreLabels';
import { isTypeLabel } from 'Components/02-molecules/tyre-labels/models/tyre-labels';
import AlternativeProductsDialog from 'Components/03-organisms/alternative-products-dialog/AlternativeProductsDialog';
import addToCartMixin from 'Libs/mixins/addToCartMixin';

const CAR_ATTRIBUTES_LIMIT = 6;

export default {
  name: 'PlpProduct21',
  components: {
    AdvancedImage,
    ProductInfoSections,
    PdpActions,
    ProductAvailability,
    PriceInfo21,
    Button,
    NoImageSvg,
    Dialog,
    PlpDescription21,
    Icon,
    MaskedLink,
    PdpVehicleNotification,
    ProductBundles,
    TyreLabels,
    AlternativeProductsDialog,
  },
  mixins: [globalMixin, addToCartMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },
    position: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      imageHover: false,
      quantity: this.product.quantityAmount,
      carAttributesLimit: CAR_ATTRIBUTES_LIMIT,
    };
  },
  computed: {
    ...mapState('carSelection', {
      vehicleName: ({ data: { vehicle = {} } }) => {
        const { manufacturer, model, type, bodyCodes } = vehicle;
        const selectionName = `${manufacturer} ${model} ${type}`;
        if (bodyCodes && bodyCodes.length > 0) {
          return `${selectionName} (${[...bodyCodes].sort().join(', ')})`;
        }
        return selectionName;
      },
    }),
    productBundles() {
      const setProductsItems = this.product.setProductsItems || [];
      const partsListItems = this.product.partsListItems || [];

      return setProductsItems.concat(partsListItems);
    },
    productVehicleCompatibility() {
      return this.product?.pdpCarBar?.key || 'UNIVERSAL_PRODUCT';
    },
    shouldShowDescription() {
      if (!this.product.shortDescription) {
        return false;
      }
      if (this.product.shortDescription.length === 0) {
        return false;
      }
      if (this.attributes.length > 0) {
        return false;
      }
      return true;
    },
    foundByText() {
      if (!this.cleanResouceType) {
        return null;
      }

      const reasonTranslationKey = this.$t(
        `plp-product-21.found_reason.${this.cleanResouceType}`
      );
      let reason = reasonTranslationKey;

      if (this.cleanResouceType === 'oe_number') {
        reason = reasonTranslationKey.replace(
          '%manufacturer%',
          this.product.resultSourceIdentifier
        );
      }

      return this.$t('plp-product-21.found_via').replace('%reason%', reason);
    },
    cleanResouceType() {
      // clean values that are treated the same
      if (this.product.resultSourceType === 'display_sku') {
        return 'sku';
      }
      if (this.product.resultSourceType === 'competitor_part_number') {
        return 'trade_number';
      }

      return this.product.resultSourceType;
    },
    detailsButtonClass() {
      return ['button--secondary', 'qa-ProductCard__showDetails'];
    },
    ...mapState('core', {
      customerServicePhoneNumber: ({ salesChannel }) => {
        return salesChannel.config.customerServicePhoneNumber;
      },
      isNewWishlist: ({ salesChannel }) =>
        salesChannel.config.isNewWishlist || false,
    }),
    attributes() {
      if (!this.product || !this.product.attributes) return [];

      return this.product.attributes
        .map((attribute) => ({
          label: attribute.title,
          info: attribute.value,
          isTop: attribute.isTop,
          id: attribute.id,
        }))
        .filter((attr) => !isTypeLabel(attr?.id));
    },
    isSkuDisplayed() {
      // Only displayed internally for CustomerCar (same as when productAvailability is Displayed)
      return this.product?.productAvailability?.length > 0;
    },
  },
  methods: {
    upperCamelCase: (string) => upperFirst(camelCase(string)),
    ...mapActions('dataLayer', ['trackProductClick']),
    handleAddToWishlist() {
      this.isNewWishlist
        ? this.$emit('wish-click', true)
        : this.addToWishlist({
            product: this.product,
            quantity: this.selectedQuantity,
            list: 'Product Listing Page',
          });
    },
    titleHover(active) {
      this.imageHover = active;
    },
    handleDocumentClick(index, { target }) {
      const documentLinkComponent = this.$refs.documentLink[index];

      if (documentLinkComponent && documentLinkComponent.$el !== target) {
        documentLinkComponent.$el.click();
      }
    },
    productClick() {
      this.trackProductClick({
        product: this.product,
        position: this.position,
      }).finally(() => {
        if (this.product.productUrl) {
          window.location.href = this.product.productUrl;
        }
      });
    },
    ...mapActions('wishlist', ['addToWishlist']),
    processIconName(icon) {
      icon = this.upperCamelCase(icon);
      return icon.includes('Pdf') ? 'NewDocumentPdf' : icon;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';
@import 'mixins';

.plp-product-21 {
  border: 1px solid var(--color-alto-sec);
  display: flex;
  flex-wrap: wrap;
  min-height: 225px;
  border-radius: 8px;
  padding: var(--space-2--half) var(--space-2) 0;

  @media #{$_mediaMDown} {
    border: 0;
    margin-left: calc(var(--space-2) * -1);
    margin-right: calc(var(--space-2) * -1);
    width: calc(100% + var(--space-2) * 2) !important;
  }

  &__actions {
    max-width: 267px;

    @media #{$_mediaLDown} {
      max-width: 100%;
    }

    ::v-deep {
      .wishlistSvg {
        margin-top: -3px;
        height: 24px !important;
      }

      .pdp-actions__in-the-cart {
        width: 100%;
        margin-top: var(--space-1);

        @media #{$_mediaLDown} {
          width: 186px;
        }

        .cartSvg {
          @media #{$_mediaLDown} {
            display: none;
          }
        }
      }

      .inner .icon + .plp-product-21__title {
        margin-left: 0;
      }
    }
  }

  &__vehicle-notification {
    @media #{$_mediaLDown} {
      margin: var(--space-1--half) 0;
      border-radius: var(--space-1);
    }

    @media #{$_mediaMDown} {
      border-radius: var(--space-1);
      margin: 0;
    }
  }
}

/* Removed unused .plp-product-21__car-attributes styles */

.foundByIdentifier {
  padding: 12px;
  background: var(--color-concrete);
  margin-bottom: 12px;
  vertical-align: middle;
  display: flex;
  line-height: 1.25;

  .icon {
    height: 22px;
    width: 21px;
    margin-right: var(--space-1);
  }

  &.isMobile {
    margin: var(--space-1--half) 0;
    border-radius: var(--space-1);
  }

  ::v-deep strong {
    font-weight: bold;
  }

  &__info {
    display: inline-block;
    margin-right: 5px;
    margin-bottom: -7px;
    margin-top: -2px;
    flex: 0 0 auto;
  }
}

.imageContainer,
.actions {
  padding: 12px;
}

/* Removed unused .pdpInformation sibling margin and .plp-product-21__show-more */

.imageContainer {
  position: relative;
  width: 25%;
}

.plp-product-21__no-image {
  max-height: 126px;
}

.contentContainer {
  padding: 0;
  text-align: left;
}

.media {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: var(--space-3);

  @media #{$_mediaLDown} {
    margin-bottom: 22px;
  }
}

.plp-product-21__cta-container {
  display: flex;
  flex-direction: column;

  @media #{$_mediaLDown} {
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }

  @media #{$_mediaSDown} {
    margin-top: var(--space-1);
  }
}

.products-availability-wrapper {
  padding: var(--space-XXS) var(--space-2);
  width: 100%;
  border: 1px solid var(--color-alt-gray);
  background: white;
  margin-top: -2px;
  z-index: 2;
  position: relative;
}

div.price {
  padding-bottom: 0;
}

.plp-product-21__title {
  color: var(--color-black);
  display: inline;
  cursor: pointer;
  margin-bottom: var(--space-1);
  font-weight: bold;
  font-size: var(--font-size-MLL);

  &:hover {
    text-decoration: underline;
  }

  @media #{$_mediaLDown} {
    display: block;
    font-size: var(--font-size-MML);
    margin-bottom: var(--space-1);
  }
}

.productId,
.productSku {
  cursor: text;
  font-size: var(--font-size-M);
  line-height: 17px;
  display: inline;
  position: relative;
  bottom: 1px;
  color: var(--color-black);
  margin-left: var(--space-1);

  @media #{$_mediaLDown} {
    font-size: var(--font-size-SM);
    margin-left: 0;
  }
}

/* Removed unused .dangerous class */

.further-details {
  ::v-deep {
    .pdp-information-21__headline {
      cursor: default;
    }
  }
}

.price {
  padding-bottom: 10px;
}

.further-details {
  .hazardousGood {
    border-right: none !important;
  }
}

.actions {
  padding: var(--space-1) var(--space-1--half);
  width: 100%;
  background: var(--color-wild-sand);
  border-top: 1px solid var(--color-alt);
  order: 2;
}

.plp-product-21__show-details {
  margin-top: var(--space-1);
  margin-bottom: var(--space-1--half);
  height: 48px;
  max-width: 267px;

  @media #{$_mediaLDown} {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
}

.plp-product-21__product-image {
  max-height: 140px;
  max-width: 100%;
  cursor: pointer;
  height: auto;
  width: auto;

  ::v-deep .img {
    transition: transform var(--time-S) ease-in-out;
    height: 140px;
    width: 180px;

    @media #{$_mediaLDown} {
      height: 80px;
      max-width: 100%;
      width: auto;
    }
  }
}

.plp-product-21__documents {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  text-align: left;
  max-width: calc(100% + var(--space-3--half));
  margin-top: var(--space-3);
}

.plp-product-21__document {
  display: flex;
  flex-direction: column;
  margin: 0 14px var(--space-2) 14px;
  cursor: pointer;
  word-break: break-word;

  @media #{$_mediaMDown} {
    max-width: 70px;
  }
}

.plp-product-21__document-icon {
  height: 35px;

  @media #{$_mediaMDown} {
    height: 32px;
  }
}

.plp-product-21__document-link {
  width: 100%;
  margin-top: var(--space-1);
  font-size: 14px;
  line-height: 13px;
  color: var(--color-mine-shaft);
  text-align: center;
}

.plp-product-21__brand {
  width: 40% !important;
  min-width: 50px;
  max-width: 100%;
  margin-top: 6%;
  height: min-content;

  @media #{$_mediaSDown} {
    margin-top: 10%;
  }
}

.plp-product-21__images {
  display: flex;
  margin-top: 3px;
  flex-direction: column;
  align-items: center;
}

@media #{$_mediaSUp} {
  .imageContainer {
    right: 3px;
  }
}

@media #{$_mediaMUp} {
  .plp-product-21__product-image {
    &:hover,
    &.active {
      ::v-deep .img {
        transform: scale(1.06);
      }
    }
  }

  .tyre-labels {
    padding-left: var(--space-1--half);
  }
}

@media #{$_mediaXLUp} {
  .plp-product-21__cta-container {
    width: 268px;
    box-sizing: content-box;
    flex-shrink: 0;
    padding: 0 0 0 var(--space-3);
    margin-top: -3px;
    align-items: flex-end;
  }

  .imageContainer {
    max-width: 180px;
    flex-shrink: 0;
    padding: 0 0 var(--space-2) 0;
    margin-right: 15px;
  }

  .contentContainer {
    width: 408px;
  }

  .media {
    align-items: center;
    flex-direction: row;
  }

  .media__icon {
    display: none;
  }

  .price {
    margin: 0;
    width: 268px;
    padding-left: 0;
  }

  .actions {
    padding: 0;
    border-top: none;
  }
}

@media #{$_mediaMDown} {
  .dangerous,
  .further-details {
    width: 100%;
    margin-left: 0;
    padding-left: 0;
  }
}

@media #{$_mediaMUp} {
  .plp-product-21__product-image {
    &:hover,
    &.active {
      ::v-deep .img {
        transform: scale(1.06);
      }
    }
  }
}

@media #{$_mediaLDown} {
  .isDesktop {
    display: none;
  }

  .plp-product-21__product-image {
    display: flex;
    justify-content: center;
    max-width: 150px;
  }
}

@media #{$_mediaXLUp} {
  .isMobile {
    display: none;
  }
}

@media #{$_mediaSDown} {
  .imageContainer {
    width: 40%;
    padding: 0;
  }

  .contentContainer {
    padding: 0;
  }

  .contentContainer {
    width: 60%;
  }

  .price {
    width: 100%;
  }

  .brand {
    margin: 11px auto;
  }

  .productId,
  .plp-product-21__title,
  .price {
    padding-left: 3px;
  }
}

@media #{$_mediaLDown} {
  .wishlistButton {
    display: none;
  }

  .products-availability-wrapper {
    display: none;
  }
}

@media all and (min-width: 1001px) and (max-width: 1300px) {
  .contentContainer {
    width: 228px;
  }
}
</style>
